import View from '@core/View';
import User from '../User';
import ItemSummaryDialog from './ItemSummaryDialog';
import {Cart} from './Cart';
import CartQuickAddItem from './CartQuickAddItem';
import pathOr from 'ramda/src/pathOr';
import {toCurrency} from '@util/NumberUtil';
import {jqueryEasing, jqueryToggleQtySelect, Backbone, ProductConfiguratorApp, ProductConfiguratorStyles} from '@modules/lazy-imports';
import isSiteFeatureEnabled from '@util/fn/isSiteFeatureEnabled';
import {calculateMaxInputQuantityForProduct} from '@modules/products/util';
import {LOGIN_URL_WITH_REDIRECT, STORE_URL, toProductUrl} from '@util/URLUtils';
import namespace from '../../upi/namespace';
import {withItemListAttribution} from './cartAttribution';

const {FREE_SHIPPING_GOAL} = upi.conf;

const CartQuickView = ItemSummaryDialog.extend( {

    dataLayer: upi.dataLayer.cart,

    _configuratorApp: null,
    _configuratorInitToken: null,
    _quickAddDialog: null,
    _quickAddItemAdditions: null,
    _quickAddFailed: false,
    _quickAddRecommendationsRequested: null,

    init: async function () {
        await jqueryEasing();

        const summaryType = isSiteFeatureEnabled( 'FreeShippingMsg' ) ?
            'cartSummaryShippingMsg' :
            'cartQuickViewSummary';

        this.cartSummaryView = new View( summaryType );
        this.cartQuickViewItemDetailView = new View( 'cartQuickViewItemDetail' );
        this.cartQuickViewConfiguratorView = new View( 'cartQuickViewConfigurator' );
        this.cartQuickViewAddConfirmationView = new View( 'cartQuickViewAddConfirmation' );

        this.loadModules().then( modal => {
            this.quickAdd = new CartQuickAddItem( modal.selector );
        } );

        Cart.bind( 'quickadd:item', ( e, data ) => {
            this.destroyConfigurator();
            this.renderSummary( data );
        } );
        Cart.bind( 'quickadd:itemreconciled', ( e, data ) => this.reconcileSummary( data ) );
        Cart.bind( 'quickadd:itemfailed', ( e, data ) => this.renderAddFailure( data ) );
    },

    renderSummary: function ( cart ) {
        var lineItems = this.serializeLineItems( cart.itemAdditions ),
            cartSummaryData = this.serializeCartSummary( cart ),
            dialogTmpl = $( this.itemSummaryLayoutView.render( {
                quantityTotal: this.getQuantitiesFor( lineItems ),
                skus: lineItems
            } ) );

        var addConfirmationTmpl = $( this.cartQuickViewAddConfirmationView.render( {
                skus: lineItems.map( line => ({
                    ...line,
                    imgSrc: this.getItemImageSource( line )
                }) ),
                cart: cartSummaryData
            } ) ),
            summaryTmpl = this.cartSummaryView.render( cartSummaryData );

        // append item details to qv-layout
        dialogTmpl.find( '#qv-order-summary' ).html( summaryTmpl );
        dialogTmpl.find( '#qv-content' ).html( addConfirmationTmpl );

        this._quickAddDialog = dialogTmpl;
        this._quickAddItemAdditions = cart.itemAdditions;
        this._quickAddFailed = false;
        this._quickAddRecommendationsRequested = null;

        this.modal.open( {
            position: this.modalOptions.position,
            width: this.modalOptions.width,
            content: dialogTmpl
        } );

        this.continueButton.focus();

        if ( isSiteFeatureEnabled( 'FreeShippingMsg' ) ) {
            setTimeout( this.animateMeter.bind( this, dialogTmpl, cart ), 0 );
        }
    },

    reconcileSummary: function ( cart, renderRecommendations = true ) {
        if ( !this._quickAddDialog || this._quickAddItemAdditions !== cart.itemAdditions ) {
            return;
        }

        const cartSummaryData = this.serializeCartSummary( cart );
        this._quickAddDialog
            .find( '#qv-order-summary' )
            .html( this.cartSummaryView.render( cartSummaryData ) );

        if ( isSiteFeatureEnabled( 'FreeShippingMsg' ) ) {
            setTimeout( this.animateMeter.bind( this, this._quickAddDialog, cart ), 0 );
        }

        if ( renderRecommendations ) {
            this.renderRecommendations( cart );
        }
    },

    renderRecommendations: function ( cart ) {
        const lineItems = this.serializeLineItems( cart.itemAdditions );

        if (
            this._quickAddFailed
            || this._quickAddItemAdditions !== cart.itemAdditions
            || this._quickAddRecommendationsRequested === cart.itemAdditions
            || lineItems.length !== 1
        ) {
            return;
        }

        this._quickAddRecommendationsRequested = cart.itemAdditions;
        this.recommendations
            .fetch( lineItems[0].product.sku )
            .then( recommendations => {
                if ( this._quickAddFailed || this._quickAddItemAdditions !== cart.itemAdditions ) {
                    return;
                }

                const recs = recommendations.skuRecommendations.map( ( item, index ) => ({
                        ...item,
                        position: index + 1,
                        actionField: 'Cart Addition Recommendation'
                    }) ),
                    recsHtml = recommendations.renderAccessories();

                this._quickAddDialog.find( '#qv-item-recs' ).append( recsHtml );
                const recsShown = recs.slice( 0, 3 );
                this.dataLayer.sendCrossSellItems( recsShown );
                recsShown.forEach( ( item, index ) => this.sendPromoView( item.productName, index ) );
            } );
    },

    renderAddFailure: function ( cart ) {
        if ( !this._quickAddDialog || this._quickAddItemAdditions !== cart.itemAdditions ) {
            return;
        }

        this._quickAddFailed = true;
        this.reconcileSummary( cart, false );
        this._quickAddDialog
            .find( '.msg-confirmation' )
            .addClass( 'error' )
            .attr( 'role', 'alert' )
            .attr( 'aria-live', 'assertive' )
            .text( upi.i18n.getTextFromKey(
                'lbl-addItemError',
                'Sorry, there was a problem attempting to add this item'
            ) );
        this._quickAddDialog.find( '#qv-item-recs' ).empty();
    },

    sendPromoView: function ( productName, index ) {
        const model = {
            promoId: 'none',
            promoName: 'Customers Also Bought Cart Addition',
            promoCreativeName: productName,
            promoPosition: `Customers Also Bought Cart Addition ${index + 1}`
        };
        upi.dataLayer.promotions.sendPromoView( model );
    },

    serializeCartSummary: function ( cart ) {
        const adjustedCartSummary = $.extend( {}, cart, {
            isLogged: User.isLogged(),
            cartLink: STORE_URL,
            signinLink: LOGIN_URL_WITH_REDIRECT,
            itemCount: pathOr( 0, ['lineItems', 'length'], cart ),
            subtotal: toCurrency( parseFloat( cart.subtotal ) ),
            shippingGoal: FREE_SHIPPING_GOAL,
            goalReached: cart.subtotal > FREE_SHIPPING_GOAL,
            amountRemainingForShippingGoal: cart.subtotal
                ? toCurrency( this.getAmountRemainingForShippingGoal( cart.subtotal ) )
                : null
        } );

        return adjustedCartSummary;
    },

    animateMeter: function ( el, data ) {
        var cartTotalEl = el.find( '#qv-order-total-value' ),
            msgEl = el.find( '.qv-order-shipping-msg' ),
            goalEl = el.find( '#qv-shipping-goal-value' ),
            orderSubTotal = data.subtotal,
            goalReached = data.subtotal > FREE_SHIPPING_GOAL,
            percentOfShippingGoal = this.getPercentOfShippingGoal( orderSubTotal );

        if ( !orderSubTotal ) {
            return;
        }

        // initialize transition for free shipping meter
        el.find( '.progress-meter-value' )
            .width( 0 )
            .delay( 1500 )
            .animate(
                {width: percentOfShippingGoal + '%'},
                {
                    easing: 'easeOutCubic',
                    duration: 1500,
                    step: function ( now ) {
                        var dollarAmount = parseFloat( orderSubTotal * (now / percentOfShippingGoal) );
                        cartTotalEl.text( !isNaN( dollarAmount ) ? toCurrency( dollarAmount ) : 0 );
                    },
                    complete: function () {
                        msgEl.removeClass( 'is-hidden' );
                        if ( goalReached ) {
                            goalEl.addClass( 'success' );
                        }
                    }
                }
            );
    },

    getPercentOfShippingGoal: function ( orderSubTotal ) {
        if ( !orderSubTotal ) {
            return 0;
        }

        return Math.max( Math.min( orderSubTotal / FREE_SHIPPING_GOAL, 1 ), 0 ) * 100;
    },

    getAmountRemainingForShippingGoal: function ( orderSubTotal ) {
        var goal = FREE_SHIPPING_GOAL;

        if ( !orderSubTotal ) {
            return 0;
        }
        return Math.max( Math.min( goal - orderSubTotal, goal ), 0 );
    },

    getCustomerPrice ( {customerPrice, catalogPrice} ) {
        return toCurrency( parseFloat( customerPrice || catalogPrice, 10 ) );
    },

    renderDetails: async function ( product, configData ) {
        // If config data exists and has multiple SKUs, render the configurator
        if ( configData && configData.skus && configData.skus.length > 1 ) {
            return this.renderConfiguratorDetails( product, configData );
        }

        // Fall back to single-SKU quick view
        const lineItems = this.serializeLineItems( [{product}] );
        const firstItem = lineItems[0];
        const {
            maxQuantityExceeded,
            maxInputQuantity
        } = await calculateMaxInputQuantityForProduct( {product} );

        const dialogTmpl = $( this.itemSummaryLayoutView.render( {skus: lineItems} ) );
        const itemDetailTmpl = $( this.cartQuickViewItemDetailView.render( {
            skus: lineItems,
            maxQuantityExceeded,
            maxInputQuantity,
            item: {
                ...firstItem,
                ...firstItem.product,
                displayPrice: `$${this.getCustomerPrice( firstItem.product )}`
            }
        } ) );

        // append item details to qv-layout
        jqueryToggleQtySelect().then( () =>
            itemDetailTmpl.find( '#skuQty' ).toggleQtySelect()
        );

        dialogTmpl.find( '#qv-content' ).html( itemDetailTmpl );

        this.modal.open( {
            position: this.modalOptions.position,
            width: this.modalOptions.width,
            content: dialogTmpl
        } );

        this.quickAdd.setItemList( this.itemList ).refresh();
    },

    renderConfiguratorDetails: async function ( product, configData ) {
        this.destroyConfigurator();

        const initToken = {};
        this._configuratorInitToken = initToken;
        const isCurrentInit = () => this._configuratorInitToken === initToken;

        var self = this;
        const lineItems = this.serializeLineItems( [{product}] );

        const dialogTmpl = $( this.itemSummaryLayoutView.render( {skus: lineItems} ) );
        const configuratorTmpl = $( this.cartQuickViewConfiguratorView.render( {} ) );

        dialogTmpl.find( '#qv-content' ).html( configuratorTmpl );

        this.modal.open( {
            position: this.modalOptions.position,
            width: this.modalOptions.width,
            content: dialogTmpl
        } );

        // Bind modal close to configurator teardown
        $( this.modal.selector ).on( 'modal:closecomplete.configurator', function () {
            self.destroyConfigurator();
        } );

        // Load Backbone, CSS, and initialize the configurator inside the modal
        try {
            // Ensure lodash is on window._ — custom Backbone fork reads it as a global
            var lodash = await import( /* webpackChunkName: "~~lodash" */ 'underscore' );
            if ( !isCurrentInit() ) {
                return;
            }
            window._ = window._ || lodash.default;

            await Promise.all( [Backbone(), ProductConfiguratorStyles()] );
            if ( !isCurrentInit() ) {
                return;
            }

            // Snapshot the product page's namespace state BEFORE we overwrite anything
            var app = namespace( 'apps.productpage' );
            this._savedNamespaceState = this._snapshotNamespace( app );

            var App = await ProductConfiguratorApp();
            if ( !isCurrentInit() ) {
                return;
            }
            var rootEl = dialogTmpl.find( '.qv-configurator' )[0];

            App.default.initialize( withItemListAttribution( {
                modalMode: true,
                rootEl: rootEl,
                initialSku: product.sku,
                productModel: configData
            }, this.itemList ) );

            self._configuratorApp = App.default;

            // Listen for SKU selection changes to update the modal header
            self._skuChangeHandler = function ( sku ) {
                if ( sku && sku.get( 'id' ) !== 'noop' ) {
                    self._updateHeaderForSku( dialogTmpl, sku, product );
                }
            };
            self._configSelectHandler = function ( configuration ) {
                if ( configuration ) {
                    self._updateHeaderForConfiguration( dialogTmpl, configuration, product );
                }
            };
            app.mediator.on( 'set:sku', self._skuChangeHandler );
            app.mediator.on( 'configuration:select', self._configSelectHandler );
        }
        catch ( err ) {
            if ( !isCurrentInit() ) {
                return;
            }
            console.error( '[QuickView] Failed to initialize product configurator', err );
            // Restore namespace if init failed partway through
            if ( this._savedNamespaceState ) {
                this._restoreNamespace( namespace( 'apps.productpage' ), this._savedNamespaceState );
                this._savedNamespaceState = null;
            }
        }
    },

    _updateHeaderForSku: function ( dialogTmpl, sku, originalProduct ) {
        var skuId = sku.get( 'id' ),
            displaySku = skuId.replace( /\-$/, '' ),
            kitName = sku.get( 'itemLongName' ) || sku.get( 'itemName' ) || '',
            productUrl = toProductUrl( {
                path: originalProduct.url || '/',
                sku: skuId
            } ),
            $itemName = dialogTmpl.find( '.qv-item-name' );

        $itemName.find( 'h2 a' )
            .attr( 'href', productUrl )
            .html( '<span>#' + displaySku + '</span> - ' + kitName );

        $itemName.find( 'a.link-arrow' )
            .attr( 'href', productUrl );
    },

    _updateHeaderForConfiguration: function ( dialogTmpl, configuration, originalProduct ) {
        var name = configuration.get( 'name' ) || '',
            productUrl = originalProduct.url || '/',
            $itemName = dialogTmpl.find( '.qv-item-name' );

        $itemName.find( 'h2 a' )
            .attr( 'href', productUrl )
            .text( name );

        $itemName.find( 'a.link-arrow' )
            .attr( 'href', productUrl );
    },

    // Known mutable keys set during configurator initialize/initializeCollections/initializeViews
    _namespaceKeys: [
        'mediator', 'appController', 'pageController', 'product', 'ProductModel',
        'dataLayer', 'context', 'historyStateIndex', 'skuList', 'priceModuleSkuList',
        'promotionList', 'optionDetailList', 'imageCollection', 'validator',
        'priceModuleView', 'skuItemController', 'productImageModuleView',
        'configurationList', 'configurationListView', 'optionGroupList', 'optionGroupListView'
    ],

    _snapshotNamespace: function ( app ) {
        var snapshot = {};
        this._namespaceKeys.forEach( function ( key ) {
            if ( key in app ) {
                snapshot[key] = app[key];
            }
        } );
        return snapshot;
    },

    _restoreNamespace: function ( app, snapshot ) {
        // Clear modal-set keys
        this._namespaceKeys.forEach( function ( key ) {
            delete app[key];
        } );
        // Restore saved page keys
        Object.keys( snapshot ).forEach( function ( key ) {
            app[key] = snapshot[key];
        } );
    },

    destroyConfigurator: function () {
        this._configuratorInitToken = null;

        if ( this._skuChangeHandler || this._configSelectHandler ) {
            var app = namespace( 'apps.productpage' );
            if ( app.mediator ) {
                if ( this._skuChangeHandler ) {
                    app.mediator.off( 'set:sku', this._skuChangeHandler );
                }
                if ( this._configSelectHandler ) {
                    app.mediator.off( 'configuration:select', this._configSelectHandler );
                }
            }
            this._skuChangeHandler = null;
            this._configSelectHandler = null;
        }

        if ( this._configuratorApp ) {
            try {
                this._configuratorApp.destroy();
            }
            catch ( err ) {
                console.warn( '[QuickView] Error destroying configurator', err );
            }
            this._configuratorApp = null;
        }

        // Restore the product page's namespace state
        if ( this._savedNamespaceState ) {
            this._restoreNamespace( namespace( 'apps.productpage' ), this._savedNamespaceState );
            this._savedNamespaceState = null;
        }

        if ( this.modal && this.modal.selector ) {
            $( this.modal.selector ).off( 'modal:closecomplete.configurator' );
        }
    }
} );

export default CartQuickView;
